import React, { useState } from 'react';
import { ArrowLeft, ExternalLink, User, Briefcase, Building2, Users, Globe, Gem, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const GuideAides = () => {
  const [activeTab, setActiveTab] = useState('particuliers');

  const categories = {
    particuliers: {
      icon: User,
      title: 'Particuliers',
      emoji: '👤',
      aides: [
        { label: "Aides au logement", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F12006" },
        { label: "Aides familles", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/N144" },
        { label: "Aides étudiants", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/N20398" },
        { label: "Aides propriétaires bailleurs", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/N19775" }
      ]
    },
    entrepreneurs: {
      icon: Briefcase,
      title: 'Entrepreneurs & Indépendants',
      emoji: '💼',
      aides: [
        { label: "Aides à la création (ACRE)", url: "https://entreprendre.service-public.gouv.fr/actualites/A18795" },
        { label: "Aides financières indépendants", url: "https://www.urssaf.fr" },
        { label: "Aides régionales", url: "https://entreprendre.service-public.gouv.fr/vosdroits/F20668" }
      ]
    },
    associations: {
      icon: Users,
      title: 'Associations',
      emoji: '🏛️',
      aides: [
        { label: "Subventions publiques", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F1599" },
        { label: "Aides locales", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/N31931" },
        { label: "Aides à l'emploi (PEC)", url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F32477" }
      ]
    },
    entreprises: {
      icon: Building2,
      title: 'Entreprises',
      emoji: '🏢',
      aides: [
        { label: "Aides à l'embauche", url: "https://entreprendre.service-public.gouv.fr/vosdroits/F23263" },
        { label: "Aides à l'apprentissage", url: "https://www.alternance.emploi.gouv.fr" },
        { label: "Aides handicap", url: "https://www.agefiph.fr" },
        { label: "Aides via France Travail", url: "https://entreprise.francetravail.fr" },
        { label: "Financements & subventions", url: "https://www.bpifrance.fr" }
      ]
    },
    dubai: {
      icon: Globe,
      title: 'Création d\'entreprise à Dubaï',
      emoji: '🌍',
      aides: [
        { label: "Zones franches", url: "https://www.dubaifreezones.com" },
        { label: "Department of Economy & Tourism", url: "https://www.dubaided.gov.ae" },
        { label: "Dubai Chamber", url: "https://www.dubaichamber.com" },
        { label: "Invest in Dubai (officiel)", url: "https://www.investindubai.gov.ae/en/business-setup" }
      ]
    }
  };

  const tabs = [
    { id: 'particuliers', label: 'Particuliers', icon: User },
    { id: 'entrepreneurs', label: 'Entrepreneurs', icon: Briefcase },
    { id: 'associations', label: 'Associations', icon: Users },
    { id: 'entreprises', label: 'Entreprises', icon: Building2 },
    { id: 'dubai', label: 'Dubaï', icon: Globe }
  ];

  const currentCategory = categories[activeTab];

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#C9A55C] hover:text-[#C9A55C]/80 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-semibold tracking-[0.3em] uppercase text-[#C9A55C]">
            Ressources officielles
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mt-4 leading-tight">
            Aides Officielles
          </h1>
          <p className="text-lg text-white/60 mt-6 max-w-2xl mx-auto">
            Accès direct aux dispositifs institutionnels. HELP'ADN analyse votre situation, 
            prépare vos dossiers et réalise vos demandes.
          </p>
          <p className="text-[#C9A55C] mt-4 font-medium">
            Accompagnement sur devis personnalisé.
          </p>
        </div>

        {/* Aide Carburant 2026 - Section visible pour tous */}
        <div className="p-6 bg-[#0d0d0d] border-2 border-[#C9A55C] mb-12">
          <h3 className="text-xl font-serif font-bold text-[#C9A55C] mb-4">
            Aide carburant 2026 — Informations essentielles
          </h3>
          <p className="text-white/70 mb-4">
            L'aide carburant 2026 concerne les travailleurs modestes qui utilisent leur véhicule pour leurs trajets domicile–travail. Elle prend la forme d'un remboursement d'environ 20 centimes par litre.
          </p>
          
          <div className="mb-4">
            <p className="text-[#C9A55C] font-semibold mb-2">Conditions principales :</p>
            <ul className="space-y-1">
              <li className="flex items-start gap-2 text-white/70">
                <span className="text-[#C9A55C]">•</span>
                Revenu fiscal de référence (RFR) ≤ 15 600 € par part
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <span className="text-[#C9A55C]">•</span>
                Utilisation d'un véhicule personnel pour aller travailler
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <span className="text-[#C9A55C]">•</span>
                Situation professionnelle active
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <p className="text-[#C9A55C] font-semibold mb-2">Procédure :</p>
            <p className="text-white/70">La demande doit être effectuée directement par l'usager sur impots.gouv.fr. L'administration n'envoie aucune notification : aucune lettre, aucun mail. L'usager doit déclencher lui-même la demande lors de l'ouverture du formulaire (prévue fin mai 2026).</p>
          </div>

          <div className="p-4 bg-[#C9A55C]/10 border border-[#C9A55C]/30">
            <p className="text-[#C9A55C] font-semibold mb-1">Important :</p>
            <p className="text-white/70 text-sm">Les informations peuvent évoluer selon les communications officielles de l'État. L'usager doit vérifier les conditions exactes sur impots.gouv.fr au moment de la demande.</p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#C9A55C] text-black'
                  : 'border border-[#C9A55C]/30 text-white/70 hover:border-[#C9A55C] hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Category Content */}
        <div className="border border-[#C9A55C]/30 p-8 mb-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-3xl">{currentCategory.emoji}</span>
            <h2 className="text-2xl font-serif font-bold text-white">
              {currentCategory.title}
            </h2>
          </div>
          
          <div className="space-y-4">
            {currentCategory.aides.map((aide, index) => (
              <a
                key={index}
                href={aide.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 border border-[#C9A55C]/20 hover:border-[#C9A55C] hover:bg-[#C9A55C]/5 transition-all group"
              >
                <span className="text-white/80 group-hover:text-white">{aide.label}</span>
                <ExternalLink className="w-4 h-4 text-[#C9A55C]" />
              </a>
            ))}
          </div>
        </div>

        {/* CTA Box - Service Premium */}
        <div className="p-8 border-2 border-[#C9A55C] bg-gradient-to-b from-[#C9A55C]/10 to-transparent">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Gem className="w-8 h-8 text-[#C9A55C]" />
            <h3 className="text-2xl font-serif font-bold text-white">
              Accompagnement HELP'ADN
            </h3>
          </div>
          
          <p className="text-center text-white/70 mb-6">
            Service premium sur devis :
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
            {[
              "Identification des aides éligibles",
              "Constitution complète des dossiers",
              "Gestion des justificatifs",
              "Dépôt des demandes",
              "Suivi jusqu'à validation"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-white/70">
                <span className="text-[#C9A55C]">•</span>
                {item}
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-[#C9A55C] text-black px-8 py-4 font-semibold tracking-wider uppercase text-sm hover:bg-[#C9A55C]/90 transition-all"
            >
              Demander un devis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <p className="text-sm text-white/40 pt-8 text-center">
          Dernière mise à jour : Mars 2026 — Sources officielles
        </p>
      </div>
    </div>
  );
};

export default GuideAides;
